import {
    DataTable,
    DataTableColumnHeader,
    DataTableControls,
    DataTablePagination,
} from 'src/registry/components/table';
import * as React from 'react';
import { createColumnHelper } from '@tanstack/react-table';
import { CellLinkWithIcon } from 'src/registry/components/table/cells/CellLinkWithIcon';
import { commonMessages } from 'src/utils/messages';
import { Customer } from 'src/features/customers/utils/types';
import useSearch from 'src/registry/hooks/useSearch';
import { useListCustomers } from 'src/features/customers/api/useListCustomers';
import { useIntl } from 'react-intl';
import { useTitleStore } from 'src/stores/useTitleStore';
import { customersMessages } from 'src/features/customers/utils/messages';
import { billingMessages } from 'src/features/billing/utils/messages';
import CellCustomersActions from 'src/features/customers/components/tables/CellCustomersActions';
import CreateCustomerFormModal from 'src/features/customers/components/forms/CreateCustomerFormModal';
import usePagination from 'src/registry/hooks/usePagination';
import useHasPermissions from 'src/utils/useHasPermissions';
import { PERMISSION } from 'src/features/auth/types';
import { INPUT_TYPE } from 'src/registry/components/table/utils/constants';

function CustomersTable() {
    const { formatMessage } = useIntl();

    const { setTitle, removeTitle } = useTitleStore();

    React.useEffect(() => {
        setTitle(formatMessage(customersMessages.customers));
        return removeTitle;
    }, [formatMessage, removeTitle, setTitle]);
    const { querySearchOptions, ...search } = useSearch({ globalFilterKey: 'name' });
    const { queryPaginationOptions, ...pagination } = usePagination();

    const permissionManageCustomers = useHasPermissions([PERMISSION.MANAGE_CUSTOMERS]);

    const {
        data: customersData,
        isLoading,
        isError,
    } = useListCustomers({
        ...querySearchOptions,
        ...queryPaginationOptions,
    });

    const columnHelper = createColumnHelper<Customer>();

    const columns = [
        columnHelper.accessor('name', {
            header: DataTableColumnHeader,
            cell: (ctx) => <CellLinkWithIcon {...ctx} to={`/customers/${ctx.row.original.id}`} />,
            meta: {
                title: formatMessage(commonMessages.name),
            },
        }),
        columnHelper.accessor('email', {
            header: DataTableColumnHeader,
            enableHiding: true,
            meta: {
                title: formatMessage(commonMessages.email),
            },
        }),
        columnHelper.accessor('uic', {
            header: DataTableColumnHeader,
            enableHiding: true,
            meta: {
                title: formatMessage(billingMessages.legalNumber),
            },
        }),
        columnHelper.accessor('tax_registration_number', {
            header: DataTableColumnHeader,
            enableHiding: true,
            meta: {
                title: formatMessage(billingMessages.taxNumber),
            },
        }),

        columnHelper.accessor('external_customer_id', {
            header: DataTableColumnHeader,
            enableHiding: true,
            enableColumnFilter: true,
            meta: {
                title: formatMessage(customersMessages.externalCustomerId),
                inputType: INPUT_TYPE.TEXT,
            },
        }),
        columnHelper.display({
            id: 'actions',
            header: DataTableColumnHeader,
            cell: CellCustomersActions,
            maxSize: 45,
        }),
    ];

    return (
        <DataTable
            columns={columns}
            data={customersData}
            semantics={{
                title: formatMessage(customersMessages.customers),
                entryName: formatMessage(customersMessages.customer).toLowerCase(),
                entryNames: formatMessage(customersMessages.customersTableCount).toLowerCase(),
                placeholder: formatMessage(customersMessages.customers).toLowerCase(),
            }}
            search={search}
            pagination={pagination}
            isError={isError}
            isLoading={isLoading}
            getRowId={(row) => row.id.toString()}
            components={{
                controls: DataTableControls,
                pagination: DataTablePagination,
                newEntry: permissionManageCustomers ? CreateCustomerFormModal : undefined,
            }}
        />
    );
}

export default CustomersTable;
